import type { GetState, SetState } from 'zustand';
import type { EchainPartsListItem } from '@hooks/states/echainPartsListSlice';
import { AssociationType } from '@libs/associations';
import type {
  AssociationProductFragmentEnhanced,
  ModelAssociationProductFragmentEnhanced,
  ProductFragmentEnhanced,
} from '@models/enhanced-fragments';
import type {
  DistinctVariant,
  MountingBracketGroup,
} from '@product/VariantSelector/MountingBracketSelector/MountingBracketSelector';
import type { SupportTrayProduct } from '@product/VariantSelector/SupportTraySelector/SupportTraySelector';
import type { EchainProductStore, EchainProductStoreInitialData } from './useEchainProductStore';

export interface EchainAccessoriesSliceData {
  groupedSupportTrays?: SupportTrayProduct[];
  groupedMountingBrackets?: MountingBracketGroup[];
  selectedMountingBracketGroup?: MountingBracketGroup | undefined;
  selectedSupportTrayProduct?: SupportTrayProduct | undefined;
  selectedDistinctVariant?: DistinctVariant | undefined;
  selectedSupportTrayVariant?: AssociationProductFragmentEnhanced | undefined;
  selectedMountingVariant?: AssociationProductFragmentEnhanced | undefined;
  hasUserDeselectedMountingBrackets?: boolean;
  hasUserDeselectedSupportTrays?: boolean;
}

export interface EchainAccessoriesSlice extends EchainAccessoriesSliceData {
  setSelectedSupportTrayProduct: (supportTray?: SupportTrayProduct) => void;
  clearSelectedSupportTray: (productId: string) => void;
  clearSelectedMountingBrackets: (productId: string) => void;
  getSelectedInteriorSeparators: (
    productId: string,
    type: AssociationType.SHELVES | AssociationType.SEPARATORS
  ) => EchainPartsListItem[] | undefined;
  setSelectedInteriorSeparator: (
    product: ProductFragmentEnhanced,
    interiorSeparator: ModelAssociationProductFragmentEnhanced | AssociationProductFragmentEnhanced,
    type: AssociationType.SHELVES | AssociationType.SEPARATORS,
    locale: string,
    value?: number
  ) => Promise<void>;
  clearInteriorSeparators: (productId: string) => void;
  setSelectedMountingBracketGroup: (mountingBracketGroup: MountingBracketGroup | undefined) => void;
  setSelectedDistinctVariant: (distinctVariant: DistinctVariant | undefined) => void;
  setSelectedSupportTrayVariant: (distinctVariant: AssociationProductFragmentEnhanced | undefined) => void;
  setSelectedMountingVariant: (mountingVariant: AssociationProductFragmentEnhanced | undefined) => void;
  setHasUserDeselectedMountingBrackets: (hasUserDeselectedMountingBrackets: boolean) => void;
  setHasUserDeselectedSupportTrays: (hasUserDeselectedSupportTrays: boolean) => void;
  setGroupedMountingBracketsForCurrentProduct: (groupedMountingBrackets: MountingBracketGroup[] | []) => void;
  setGroupedSupportTraysForCurrentProduct: (groupedSupportTrays: SupportTrayProduct[] | []) => void;
}

export const createEchainAccessoriesSlice = (
  set: SetState<EchainProductStore>,
  get: GetState<EchainProductStore>,
  initialData?: EchainProductStoreInitialData
) =>
  ({
    groupedSupportTrays: initialData?.groupedSupportTrays ?? undefined,
    groupedMountingBrackets: initialData?.groupedMountingBrackets ?? undefined,
    selectedMountingBracketGroup: initialData?.selectedMountingBracketGroup ?? undefined,
    selectedDistinctVariant: initialData?.selectedDistinctVariant ?? undefined,
    selectedMountingVariant: initialData?.selectedMountingVariant ?? undefined,
    selectedSupportTrayProduct: initialData?.selectedSupportTrayProduct ?? undefined,
    selectedSupportTrayVariant: initialData?.selectedSupportTrayVariant ?? undefined,
    hasUserDeselectedMountingBrackets: false,
    hasUserDeselectedSupportTrays: false,
    getSelectedSupportTray: () =>
      get().eChainPartsList?.partsList.find(
        partsListItem => partsListItem.associationType === AssociationType.SUPPORT_TRAY
      ),
    setSelectedSupportTrayProduct: (supportTray?: SupportTrayProduct) => {
      get().setHasUserDeselectedSupportTrays(false);
      set(
        (state): EchainProductStore => ({
          ...state,
          selectedSupportTrayProduct: supportTray,
        })
      );
    },
    clearSelectedSupportTray: () => {
      get().setSelectedSupportTrayVariant(undefined);
      get().setSelectedSupportTrayProduct(undefined);
      get().removeEchainPartsListItemsByAssociationTypes([AssociationType.SUPPORT_TRAY]);
    },
    clearSelectedMountingBrackets: () => {
      get().setSelectedMountingVariant(undefined);
      get().setSelectedDistinctVariant(undefined);
      get().setSelectedMountingBracketGroup(undefined);
      get().removeEchainPartsListItemsByAssociationTypes([AssociationType.MOUNTING_BRACKETS]);
    },
    // slice for interior separators type SHELVES|SEPARATORS
    getSelectedInteriorSeparators: (productId, type) =>
      get().eChainPartsList?.partsList.filter(_item => _item.associationType === type),
    setSelectedInteriorSeparator: async (product, interiorSeparator, type, locale, value = 1) => {
      const checkInteriorSeparator = get().getEchainPartsListItem(interiorSeparator.id);
      if (checkInteriorSeparator) {
        get().setEchainPartsListItemQuantity(interiorSeparator.id, value);
      } else {
        await get().addEchainPartsListItem(interiorSeparator, locale, type, value);
      }
    },
    clearInteriorSeparators: () => {
      get().removeEchainPartsListItemsByAssociationTypes([AssociationType.SHELVES]);
      get().removeEchainPartsListItemsByAssociationTypes([AssociationType.SEPARATORS]);
    },
    setSelectedMountingBracketGroup(mountingBracketGroup: MountingBracketGroup) {
      if (mountingBracketGroup) {
        get().setHasUserDeselectedMountingBrackets(false);
      }
      set(
        (state): EchainProductStore => ({
          ...state,
          selectedMountingBracketGroup: mountingBracketGroup,
        })
      );
    },
    setSelectedDistinctVariant(distinctVariant: DistinctVariant) {
      if (distinctVariant) {
        get().setHasUserDeselectedMountingBrackets(false);
      }
      set(
        (state): EchainProductStore => ({
          ...state,
          selectedDistinctVariant: distinctVariant,
        })
      );
    },
    setSelectedSupportTrayVariant(variant: AssociationProductFragmentEnhanced) {
      if (variant) {
        get().setHasUserDeselectedSupportTrays(false);
      }
      set(
        (state): EchainProductStore => ({
          ...state,
          selectedSupportTrayVariant: variant,
        })
      );
    },
    setSelectedMountingVariant(mountingVariant: AssociationProductFragmentEnhanced) {
      if (mountingVariant) {
        get().setHasUserDeselectedMountingBrackets(false);
      }
      set(
        (state): EchainProductStore => ({
          ...state,
          selectedMountingVariant: mountingVariant,
        })
      );
    },
    setGroupedMountingBracketsForCurrentProduct: (groupedMountingBrackets: MountingBracketGroup[] = []) => {
      set(
        (state): EchainProductStore => ({
          ...state,
          groupedMountingBrackets,
        })
      );
    },
    setGroupedSupportTraysForCurrentProduct: (groupedSupportTrays: SupportTrayProduct[] = []) => {
      set(
        (state): EchainProductStore => ({
          ...state,
          groupedSupportTrays,
        })
      );
    },
    setHasUserDeselectedMountingBrackets: (hasUserDeselectedMountingBrackets: boolean) => {
      set(
        (state): EchainProductStore => ({
          ...state,
          hasUserDeselectedMountingBrackets,
        })
      );
    },
    setHasUserDeselectedSupportTrays: (hasUserDeselectedSupportTrays: boolean) => {
      set(
        (state): EchainProductStore => ({
          ...state,
          hasUserDeselectedSupportTrays,
        })
      );
    },
  } as EchainAccessoriesSlice);
